import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X, Image, GripVertical, Upload, Link } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { neighborhoodsAPI, uploadAPI } from '../../utils/api';

export const GalleryManager = () => {
  const { toast } = useToast();
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isAdding, setIsAdding] = useState(false);
  const [newNeighborhood, setNewNeighborhood] = useState({
    name: '',
    description: '',
    highlights: [''],
    photos: [{ url: '', caption: '' }],
    order: 0,
    active: true
  });

  const fetchNeighborhoods = useCallback(async () => {
    try {
      const response = await neighborhoodsAPI.getAllAdmin();
      setNeighborhoods(response.data);
    } catch (error) {
      console.error('Error fetching neighborhoods:', error);
      toast({
        title: "Error",
        description: "Failed to load galleries.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchNeighborhoods();
  }, [fetchNeighborhoods]);

  const handleAdd = async () => {
    if (!newNeighborhood.name || !newNeighborhood.description) {
      toast({ title: "Error", description: "Name and description are required.", variant: "destructive" });
      return;
    }
    try {
      const dataToSend = {
        ...newNeighborhood,
        highlights: newNeighborhood.highlights.filter(h => h.trim() !== ''),
        photos: newNeighborhood.photos.filter(p => p.url.trim() !== '')
      };
      await neighborhoodsAPI.create(dataToSend);
      toast({ title: "Gallery Created", description: "Neighborhood gallery has been created." });
      setIsAdding(false);
      setNewNeighborhood({
        name: '', description: '', highlights: [''], photos: [{ url: '', caption: '' }], order: 0, active: true
      });
      fetchNeighborhoods();
    } catch (error) {
      console.error('Error creating neighborhood:', error);
      toast({ title: "Error", description: "Failed to create gallery.", variant: "destructive" });
    }
  };

  const handleEdit = (neighborhood) => {
    setEditingId(neighborhood.id);
    setEditForm({
      ...neighborhood,
      highlights: neighborhood.highlights.length > 0 ? neighborhood.highlights : [''],
      photos: neighborhood.photos.length > 0 ? neighborhood.photos : [{ url: '', caption: '' }]
    });
  };

  const handleSaveEdit = async () => {
    try {
      const dataToSend = {
        ...editForm,
        highlights: editForm.highlights.filter(h => h.trim() !== ''),
        photos: editForm.photos.filter(p => p.url.trim() !== '')
      };
      await neighborhoodsAPI.update(editingId, dataToSend);
      toast({ title: "Gallery Updated", description: "Neighborhood gallery has been updated." });
      setEditingId(null);
      setEditForm({});
      fetchNeighborhoods();
    } catch (error) {
      console.error('Error updating neighborhood:', error);
      toast({ title: "Error", description: "Failed to update gallery.", variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery?')) {
      try {
        await neighborhoodsAPI.delete(id);
        toast({ title: "Gallery Deleted", description: "Neighborhood gallery has been removed." });
        fetchNeighborhoods();
      } catch (error) {
        console.error('Error deleting neighborhood:', error);
        toast({ title: "Error", description: "Failed to delete gallery.", variant: "destructive" });
      }
    }
  };

  // Helper functions for managing arrays
  const updateHighlight = (index, value, isEdit = false) => {
    if (isEdit) {
      const updated = [...editForm.highlights];
      updated[index] = value;
      setEditForm({ ...editForm, highlights: updated });
    } else {
      const updated = [...newNeighborhood.highlights];
      updated[index] = value;
      setNewNeighborhood({ ...newNeighborhood, highlights: updated });
    }
  };

  const addHighlight = (isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, highlights: [...editForm.highlights, ''] });
    } else {
      setNewNeighborhood({ ...newNeighborhood, highlights: [...newNeighborhood.highlights, ''] });
    }
  };

  const removeHighlight = (index, isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, highlights: editForm.highlights.filter((_, i) => i !== index) });
    } else {
      setNewNeighborhood({ ...newNeighborhood, highlights: newNeighborhood.highlights.filter((_, i) => i !== index) });
    }
  };

  const updatePhoto = (index, field, value, isEdit = false) => {
    if (isEdit) {
      const updated = [...editForm.photos];
      updated[index] = { ...updated[index], [field]: value };
      setEditForm({ ...editForm, photos: updated });
    } else {
      const updated = [...newNeighborhood.photos];
      updated[index] = { ...updated[index], [field]: value };
      setNewNeighborhood({ ...newNeighborhood, photos: updated });
    }
  };

  const addPhoto = (isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, photos: [...editForm.photos, { url: '', caption: '' }] });
    } else {
      setNewNeighborhood({ ...newNeighborhood, photos: [...newNeighborhood.photos, { url: '', caption: '' }] });
    }
  };

  const removePhoto = (index, isEdit = false) => {
    if (isEdit) {
      setEditForm({ ...editForm, photos: editForm.photos.filter((_, i) => i !== index) });
    } else {
      setNewNeighborhood({ ...newNeighborhood, photos: newNeighborhood.photos.filter((_, i) => i !== index) });
    }
  };

  const handlePhotoUpload = async (index, file, isEdit = false) => {
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast({ title: "Invalid file type", description: "Please upload a JPG, PNG, GIF, WebP, or SVG image.", variant: "destructive" });
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Maximum file size is 5MB.", variant: "destructive" });
      return;
    }
    
    try {
      const response = await uploadAPI.uploadImage(file);
      updatePhoto(index, 'url', response.data.url, isEdit);
      toast({ title: "Image uploaded", description: "Photo uploaded successfully." });
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: "Upload failed", description: "Failed to upload image.", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading galleries...</div>;
  }

  const renderForm = (data, isEdit = false) => (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Neighborhood Name *</label>
          <input
            type="text"
            value={data.name}
            onChange={(e) => isEdit 
              ? setEditForm({ ...editForm, name: e.target.value })
              : setNewNeighborhood({ ...newNeighborhood, name: e.target.value })
            }
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
            placeholder="e.g., Downtown Cornwall"
            data-testid="gallery-name-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Display Order</label>
          <input
            type="number"
            value={data.order}
            onChange={(e) => isEdit 
              ? setEditForm({ ...editForm, order: parseInt(e.target.value) || 0 })
              : setNewNeighborhood({ ...newNeighborhood, order: parseInt(e.target.value) || 0 })
            }
            className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
        <textarea
          value={data.description}
          onChange={(e) => isEdit 
            ? setEditForm({ ...editForm, description: e.target.value })
            : setNewNeighborhood({ ...newNeighborhood, description: e.target.value })
          }
          className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
          rows={3}
          placeholder="Describe this neighborhood..."
          data-testid="gallery-description-input"
        />
      </div>

      {/* Highlights */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Highlights</label>
        {data.highlights.map((highlight, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={highlight}
              onChange={(e) => updateHighlight(index, e.target.value, isEdit)}
              className="flex-1 px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
              placeholder="e.g., Waterfront views"
            />
            {data.highlights.length > 1 && (
              <Button variant="outline" onClick={() => removeHighlight(index, isEdit)} className="text-red-600">
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
        <Button variant="outline" onClick={() => addHighlight(isEdit)} className="mt-2">
          <Plus className="w-4 h-4 mr-2" /> Add Highlight
        </Button>
      </div>

      {/* Photos */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Photos</label>
        {data.photos.map((photo, index) => (
          <div key={index} className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex gap-4">
              <div className="flex-1 space-y-3">
                {/* Upload/URL Toggle */}
                <div className="flex gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handlePhotoUpload(index, e.target.files[0], isEdit);
                        }
                      }}
                      className="hidden"
                      id={`photo-upload-${isEdit ? 'edit' : 'new'}-${index}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById(`photo-upload-${isEdit ? 'edit' : 'new'}-${index}`).click()}
                      className="flex items-center gap-1"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                    </Button>
                  </label>
                  <span className="text-sm text-slate-500 self-center">or paste URL below</span>
                </div>
                <input
                  type="url"
                  value={photo.url}
                  onChange={(e) => updatePhoto(index, 'url', e.target.value, isEdit)}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="Image URL (https://...) or upload above"
                />
                <input
                  type="text"
                  value={photo.caption}
                  onChange={(e) => updatePhoto(index, 'caption', e.target.value, isEdit)}
                  className="w-full px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-emerald-500"
                  placeholder="Caption"
                />
                {photo.url && photo.url.startsWith('data:') && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1">
                    <Image className="w-3 h-3" />
                    Uploaded image (works on all environments)
                  </p>
                )}
              </div>
              {photo.url && (
                <img src={photo.url} alt={photo.caption} className="w-24 h-24 object-cover rounded-lg border border-slate-200" 
                  onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              {data.photos.length > 1 && (
                <Button variant="outline" onClick={() => removePhoto(index, isEdit)} className="text-red-600 self-start">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={() => addPhoto(isEdit)} className="mt-2">
          <Image className="w-4 h-4 mr-2" /> Add Photo
        </Button>
      </div>

      {/* Active Toggle */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id={isEdit ? "active-edit" : "active-new"}
          checked={data.active}
          onChange={(e) => isEdit 
            ? setEditForm({ ...editForm, active: e.target.checked })
            : setNewNeighborhood({ ...newNeighborhood, active: e.target.checked })
          }
          className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
        />
        <label htmlFor={isEdit ? "active-edit" : "active-new"} className="text-sm font-medium text-slate-700">
          Active (visible on website)
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="gallery-manager">
      {/* Add Button */}
      {!isAdding && (
        <Button onClick={() => setIsAdding(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-gallery-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Neighborhood Gallery
        </Button>
      )}

      {/* Add Form */}
      {isAdding && (
        <Card className="border-2 border-emerald-300">
          <CardHeader className="bg-emerald-50">
            <CardTitle>Add New Neighborhood Gallery</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            {renderForm(newNeighborhood, false)}
            <div className="flex gap-3 pt-4 mt-4 border-t">
              <Button onClick={handleAdd} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-gallery-btn">
                <Save className="w-4 h-4 mr-2" /> Save Gallery
              </Button>
              <Button variant="outline" onClick={() => setIsAdding(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Existing Galleries */}
      <div className="grid gap-6">
        {neighborhoods.length === 0 ? (
          <div className="text-center py-8 text-slate-500">No galleries yet. Click "Add Neighborhood Gallery" to create one.</div>
        ) : (
          neighborhoods.map((neighborhood) => (
            <Card key={neighborhood.id} className={`border-2 ${!neighborhood.active ? 'opacity-60' : ''}`} data-testid={`gallery-card-${neighborhood.id}`}>
              <CardContent className="pt-6">
                {editingId === neighborhood.id ? (
                  <>
                    {renderForm(editForm, true)}
                    <div className="flex gap-3 pt-4 mt-4 border-t">
                      <Button onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Save className="w-4 h-4 mr-2" /> Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => { setEditingId(null); setEditForm({}); }}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="flex gap-4">
                    {/* Preview Images */}
                    <div className="flex gap-2 flex-shrink-0">
                      {neighborhood.photos.slice(0, 3).map((photo, idx) => (
                        <img key={idx} src={photo.url} alt={photo.caption} className="w-16 h-16 object-cover rounded-lg"
                          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=100'; }} />
                      ))}
                      {neighborhood.photos.length > 3 && (
                        <div className="w-16 h-16 bg-slate-200 rounded-lg flex items-center justify-center text-slate-600 text-sm">
                          +{neighborhood.photos.length - 3}
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-bold text-lg">{neighborhood.name}</h3>
                        {!neighborhood.active && <Badge variant="secondary">Hidden</Badge>}
                        <Badge variant="outline">Order: {neighborhood.order}</Badge>
                      </div>
                      <p className="text-slate-600 text-sm mb-2 line-clamp-2">{neighborhood.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {neighborhood.highlights.map((h, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{h}</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="outline" onClick={() => handleEdit(neighborhood)} data-testid={`edit-gallery-${neighborhood.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" className="text-red-600" onClick={() => handleDelete(neighborhood.id)} data-testid={`delete-gallery-${neighborhood.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
